import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { HeartHandshake, Loader2, MessageCircle, ShieldAlert, X, Ear, Flag, Heart } from "lucide-react";
import { SafetyReportDialog } from "@/components/SafetyReport";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Avatar, BackButton, Empty, Spinner } from "@/components/Common";
import { Switch } from "@/components/ui/switch";

const time = (s: string) => new Date(s).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
const ago = (s: string) => { const m = Math.max(0, Math.round((Date.now() - new Date(s).getTime()) / 60000)); return m < 1 ? "adesso" : `${m} min fa`; };

function Rules() {
  return (
    <div className="surface p-4 text-sm text-ink/75 space-y-1.5" data-testid="listen-rules">
      <p className="font-semibold text-ink inline-flex items-center gap-2"><ShieldAlert size={16} className="text-[hsl(var(--primary))]" />Persone reali, non professionisti</p>
      <p>Chi ascolta è un'altra persona di UNSEEN che si è resa disponibile: non è un medico né uno psicologo e non può garantire di rispondere subito.</p>
      <p>Se sei in pericolo o pensi di farti del male, chiama subito il 112 o guarda <Link to="/aiuto" className="underline font-semibold" data-testid="listen-help-link">Aiuto e contatti</Link>. In chat puoi bloccare chiunque in qualsiasi momento.</p>
    </div>
  );
}

function AskForm({ topics, onDone }: { topics: any[]; onDone: () => void }) {
  const [topic, setTopic] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post("/listen/requests", { topic, note }); toast.success("Richiesta inviata in forma anonima"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="surface p-4 space-y-3" data-testid="listen-ask-form">
      <div className="text-sm font-medium text-ink/75">Di cosa vorresti parlare?</div>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Argomento">
        {topics.map((t) => <button key={t.slug} type="button" role="radio" aria-checked={topic === t.slug} onClick={() => setTopic(t.slug)} className={`chip min-h-[40px] ${topic === t.slug ? "chip-lime" : ""}`} data-testid={`listen-topic-${t.slug}`}>{t.name}</button>)}
      </div>
      <textarea className="field min-h-[80px]" maxLength={280} placeholder="Vuoi aggiungere due parole? (facoltativo, senza nomi o dati personali: la leggono le persone disponibili ad ascoltare)" value={note} onChange={(e) => setNote(e.target.value)} aria-label="Nota" data-testid="listen-note-input" />
      <button className="btn btn-primary w-full" disabled={!topic || busy} onClick={send} data-testid="listen-ask-submit">{busy ? <Loader2 size={16} className="animate-spin" /> : <HeartHandshake size={16} />}Cerca qualcuno che ascolti</button>
      <p className="text-xs text-ink/55">Il tuo nome resta nascosto finché qualcuno non accetta. La richiesta dura 30 minuti.</p>
    </div>
  );
}

function MyRequest({ r, refresh }: { r: any; refresh: () => void }) {
  const cancel = async () => { try { await api.delete(`/listen/requests/${r.id}`); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  if (r.status === "accepted") return (
    <div className="surface p-4 space-y-3" data-testid="listen-request-accepted">
      <p className="font-semibold">Una persona è disponibile ad ascoltarti.</p>
      <Link to={`/messaggi/${r.conversation_id}`} className="btn btn-primary w-full" data-testid="listen-open-chat"><MessageCircle size={16} />Apri la chat</Link>
      <button className="btn btn-ghost w-full" onClick={cancel} data-testid="listen-new-request-btn">Fai una nuova richiesta</button>
    </div>
  );
  return (
    <div className="surface p-4 space-y-3" data-testid="listen-request-open">
      <p className="font-semibold inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin text-[hsl(var(--primary))]" />Stiamo cercando una persona disponibile…</p>
      <p className="text-sm text-ink/70">{r.topic_name} · valida fino alle {time(r.expires_at)}. Puoi chiudere l'app: riceverai una notifica.</p>
      <button className="btn btn-ghost" onClick={cancel} data-testid="listen-cancel-btn"><X size={15} />Annulla richiesta</button>
    </div>
  );
}

function Listener({ me, refresh }: { me: any; refresh: () => void }) {
  const nav = useNavigate();
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [reporting, setReporting] = useState<string | null>(null);
  const { data: reqs, isLoading } = useQuery({ queryKey: ["listen-open"], enabled: me.available, refetchInterval: 15000, queryFn: () => api.get("/listen/requests").then((r) => r.data) });
  const toggle = async (v: boolean) => {
    if (v && !me.terms_accepted && !terms) return toast.error("Prima accetta le regole dell'ascolto");
    try { await api.patch("/listen/availability", { available: v, accept_terms: terms }); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const accept = async (id: string) => {
    setBusy(id);
    try { const { data } = await api.post(`/listen/requests/${id}/accept`); nav(`/messaggi/${data.conversation_id}`); }
    catch (e) { toast.error(errMsg(e)); refresh(); } finally { setBusy(null); }
  };
  return (
    <section className="space-y-3" data-testid="listen-listener">
      <h2 className="font-semibold inline-flex items-center gap-2"><Ear size={18} />Vuoi ascoltare?</h2>
      <div className="surface p-1">
        {!me.terms_accepted && (
          <label className="row items-start text-sm cursor-pointer">
            <input type="checkbox" className="mt-1" checked={terms} onChange={(e) => setTerms(e.target.checked)} data-testid="listen-terms-checkbox" />
            <span className="text-ink/75">Ascolterò con rispetto, senza giudicare e senza dare consigli medici. Non chiederò dati personali e, se qualcuno è in pericolo, gli suggerirò di chiamare il 112.</span>
          </label>
        )}
        <div className="row">
          <label htmlFor="listen-switch" className="flex-1 font-medium">Sono disponibile ad ascoltare</label>
          <Switch id="listen-switch" checked={me.available} onCheckedChange={toggle} data-testid="listen-available-toggle" />
        </div>
      </div>
      {me.available && (isLoading ? <Spinner /> : !reqs?.length ? <Empty testId="listen-no-requests">Nessuno sta cercando ascolto in questo momento. Ti avviseremo con una notifica.</Empty> : (
        <div className="surface p-1" data-testid="listen-requests">
          {reqs.map((r: any) => (
            <div key={r.id} className="row items-start" data-testid="listen-request-item">
              <span className="flex-1 min-w-0"><span className="font-semibold block">{r.topic_name}</span>{r.note && <span className="text-sm text-ink/70 block break-words">“{r.note}”</span>}<span className="text-xs text-ink/55">{ago(r.created_at)}</span></span>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <button className="btn btn-primary" disabled={busy === r.id} onClick={() => accept(r.id)} data-testid="listen-accept-btn">Ascolto io</button>
                <button className="text-xs text-ink/55 inline-flex items-center gap-1 min-h-[32px]" onClick={() => setReporting(r.id)} data-testid="listen-report-btn"><Flag size={12} />Segnala</button>
              </div>
            </div>
          ))}
        </div>
      ))}
      <SafetyReportDialog url={`/listen/requests/${reporting}/report`} title="Segnala richiesta" note="Non vedrai più le richieste di questa persona. La segnalazione arriva a chi gestisce UNSEEN."
        open={!!reporting} onOpenChange={(o) => !o && setReporting(null)} onDone={refresh} />
      <ThanksReceived me={me} />
    </section>
  );
}

function ThanksReceived({ me }: { me: any }) {
  if (!me.thanks_count) return null;
  return (
    <div className="surface p-4 space-y-2" data-testid="listen-thanks-received">
      <p className="font-semibold inline-flex items-center gap-2"><Heart size={16} className="text-[hsl(var(--primary))]" />{me.thanks_count === 1 ? "Una persona ti ha ringraziato" : `${me.thanks_count} persone ti hanno ringraziato`}</p>
      {me.thanks_received.filter((t: any) => t.message).map((t: any) => <p key={t.created_at} className="text-sm text-ink/75 italic" data-testid="listen-thanks-message">“{t.message}”</p>)}
    </div>
  );
}

function ThankRow({ t, onDone }: { t: any; onDone: () => void }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post(`/listen/requests/${t.id}/thanks`, { message: msg }); toast.success("Grazie inviato"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="p-3 space-y-2" data-testid="listen-thank-item">
      <div className="flex items-center gap-2 text-sm"><Avatar user={t.listener} size={28} /><span><b>{t.listener.display_name}</b> ti ha ascoltato · {t.topic_name}</span></div>
      <div className="flex gap-2">
        <input className="field flex-1 min-w-0" maxLength={200} placeholder="Due parole (facoltativo)" value={msg} onChange={(e) => setMsg(e.target.value)} aria-label="Messaggio di ringraziamento" data-testid="listen-thank-input" />
        <button className="btn btn-primary shrink-0" disabled={busy} onClick={send} data-testid="listen-thank-btn"><Heart size={15} />Grazie</button>
      </div>
    </div>
  );
}

function ToThank({ list, refresh }: { list: any[]; refresh: () => void }) {
  if (!list?.length) return null;
  return (
    <section className="surface p-1" data-testid="listen-to-thank">
      <p className="px-3 pt-3 font-semibold">Vuoi ringraziare chi ti ha ascoltato?</p>
      {list.map((t) => <ThankRow key={t.id} t={t} onDone={refresh} />)}
    </section>
  );
}

export default function Ascolto() {
  const qc = useQueryClient();
  const { data: me, isLoading } = useQuery({ queryKey: ["listen-me"], refetchInterval: 15000, queryFn: () => api.get("/listen/me").then((r) => r.data) });
  const refresh = () => ["listen-me", "listen-open"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  return (
    <div className="px-5 md:px-12 pt-6 md:pt-12 pb-12 max-w-2xl mx-auto space-y-6" data-testid="ascolto-page">
      <BackButton parent="/" />
      <div className="space-y-2">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Parla con qualcuno</h1>
        <p className="text-ink/70">Chiedi in forma anonima: la prima persona disponibile accetta e si apre una chat privata tra voi.</p>
      </div>
      <Rules />
      {isLoading || !me ? <Spinner /> : (
        <>
          {me.request ? <MyRequest r={me.request} refresh={refresh} /> : <AskForm topics={me.topics} onDone={refresh} />}
          <ToThank list={me.to_thank} refresh={refresh} />
          <Listener me={me} refresh={refresh} />
        </>
      )}
    </div>
  );
}
